import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ChartIcon,
  Speed as SpeedIcon,
  Bolt as BoltIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ShockDashboardSkeleton from './ShockDashboardSkeleton';

/**
 * Shock Dashboard Component
 * Displays AI News Shock system statistics and visualization
 */
const ShockDashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    fetchShockData();
  }, []);

  const fetchShockData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch shock summary and config in parallel
      const [summaryRes, configRes] = await Promise.all([
        api.get('/shock/summary'),
        api.get('/shock/config')
      ]);

      setSummary(summaryRes.data);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Error fetching shock data:', err);
      setError(err.message || 'Failed to load shock data');
    } finally {
      setLoading(false);
    }
  };

  const getShockLabel = (shock) => {
    if (!shock.gating_triggered) return t('aiNews.shock.weak');
    return shock.shock_weight > 0.9 ? t('aiNews.shock.extreme') : 
           shock.shock_weight > 0.7 ? t('aiNews.shock.strong') : 
           t('aiNews.shock.moderate');
  };

  const formatPercent = (value) => `${(value * 100).toFixed(1)}%`;

  if (loading) {
    return <ShockDashboardSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('aiNews.shock.noShockData')}
      </Alert>
    );
  }

  // Prepare chart data
  const chartData = summary.top_shocks.map(shock => ({
    name: shock.indicator_code.replace(/_/g, ' '),
    value: shock.adjusted_shock,
    weight: shock.shock_weight,
    articles: shock.article_count,
    gated: shock.gating_triggered,
    originalName: shock.indicator_code
  }));

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t('aiNews.shock.pageTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('aiNews.shock.pageDescription')}
        </Typography>
      </Box>

      {/* Real-time Metrics Card */}
      <Card 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.info.main, 0.08)}`,
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            background: `linear-gradient(90deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
            px: { xs: 2, md: 3 },
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`
                }}
              >
                <BoltIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                  {t('aiNews.shock.realtimeMetrics')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {t('aiNews.shock.pageDescription').split('.')[0]}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={`${t('aiNews.shock.updated')}: ${new Date(summary.calculation_date).toLocaleDateString()}`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Summary Stats - Flexbox Layout */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            p: { xs: 2, md: 3 },
            width: '100%'
          }}
        >
          {/* Shock Rate */}
          <Box sx={{ 
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
              borderColor: alpha(theme.palette.primary.main, 0.25)
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`
                }}
              >
                <ChartIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t('aiNews.shock.shockRate')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="primary" sx={{ mb: 0.5 }}>
              {formatPercent(summary.shock_rate_pct / 100)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.shocked_indicators}/{summary.total_indicators} {t('aiNews.shock.indicators')}
            </Typography>
          </Box>

          {/* Gating Rate */}
          <Box sx={{ 
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`,
              borderColor: alpha(theme.palette.warning.main, 0.25)
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.light, 0.1)} 100%)`
                }}
              >
                <BoltIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t('aiNews.shock.gatingRate')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mb: 0.5 }}>
              {formatPercent(summary.gating_rate_pct / 100)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.gated_count} {t('aiNews.shock.shocksGated')}
            </Typography>
          </Box>

          {/* Avg Shock Weight */}
          <Box sx={{ 
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`,
              borderColor: alpha(theme.palette.info.main, 0.25)
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.light, 0.1)} 100%)`
                }}
              >
                <SpeedIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t('aiNews.shock.avgShockWeight')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="info.main" sx={{ mb: 0.5 }}>
              {formatPercent(summary.avg_shock_weight)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('aiNews.shock.newsInfluence')}
            </Typography>
          </Box>

          {/* System Status */}
          <Box sx={{ 
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
            border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.15)}`,
              borderColor: alpha(theme.palette.success.main, 0.25)
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)`
                }}
              >
                <TimelineIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t('aiNews.shock.systemStatus')}
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mb: 0.5 }}>
              {config?.global_config?.enabled ? t('aiNews.shock.active') : t('aiNews.shock.inactive')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              θ={config?.global_config?.shock_threshold || 'N/A'}, k={config?.global_config?.sensitivity || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Top Shocks Chart */}
      <Card 
        elevation={0}
        sx={{ 
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.03)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.08)}`,
          borderRadius: 3,
          overflow: 'visible'
        }}
      >
        {/* Header with gradient background */}
        <Box 
          sx={{ 
            background: `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
            px: { xs: 2, md: 3 },
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}`
                }}
              >
                <BoltIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
              <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                {t('aiNews.shock.topShocks')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {t('aiNews.shock.topShocksSubtitle')}
              </Typography>
              </Box>
            </Box>
            <Chip 
              label={`${summary.gated_count} ${t('aiNews.shock.gatedShocks')}`}
              sx={{ 
                backgroundColor: alpha(theme.palette.warning.main, 0.12),
                color: theme.palette.warning.main,
                fontWeight: 700,
                fontSize: '0.875rem',
                height: 36,
                px: 1,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ pt: 3 }}>
          
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis 
                type="number" 
                domain={[-600, 600]}
                tickFormatter={(value) => value.toFixed(0)}
                style={{ fontSize: 11 }}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={120}
                style={{ fontSize: 10 }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload[0]) {
                    const data = payload[0].payload;
                    return (
                      <Card sx={{ p: 1.5, minWidth: 220 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          {data.originalName}
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" display="block">
                          {t('aiNews.shock.adjustedShock')}: <strong>{data.value.toFixed(2)}</strong>
                        </Typography>
                        <Typography variant="caption" display="block">
                          {t('aiNews.shock.shockWeight')}: <strong>{formatPercent(data.weight)}</strong>
                        </Typography>
                        <Typography variant="caption" display="block">
                          {t('aiNews.articles')}: <strong>{data.articles}</strong>
                        </Typography>
                        <Typography variant="caption" display="block">
                          {t('aiNews.shock.status')}: <strong>{data.gated ? t('aiNews.shock.gated') : t('aiNews.shock.notGated')}</strong>
                        </Typography>
                      </Card>
                    );
                  }
                  return null;
                }}
                cursor={{ fill: alpha(theme.palette.warning.main, 0.05) }}
                wrapperStyle={{ zIndex: 1000 }}
                animationDuration={50}
                animationEasing="ease-out"
              />
              <ReferenceLine x={0} stroke={theme.palette.divider} strokeWidth={2} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.value > 0 ? theme.palette.success.main : theme.palette.error.main}
                    opacity={entry.gated ? 1 : 0.4}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed Shock Breakdown */}
      <Card
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
          borderRadius: 3,
          overflow: 'visible'
        }}
      >
        {/* Header with gradient background */}
        <Box 
          sx={{ 
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
            px: { xs: 2, md: 3 },
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
              <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                {t('aiNews.shock.detailedBreakdown')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {t('aiNews.shock.detailedBreakdownSubtitle')}
              </Typography>
              </Box>
            </Box>
            <Chip 
              label={`Top ${summary.top_shocks.length} ${t('aiNews.shock.topShocks')}`}
              sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: '0.875rem',
                height: 36,
                px: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ pt: 3 }}>
          
          <Box sx={{ overflowX: 'auto' }}>
            {summary.top_shocks.map((shock, index) => (
              <Box
                key={shock.indicator_code}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  mb: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  background: shock.gating_triggered 
                    ? alpha(shock.adjusted_shock > 0 ? theme.palette.success.main : theme.palette.error.main, 0.03)
                    : alpha(theme.palette.background.paper, 0.5),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.04),
                    borderColor: alpha(theme.palette.primary.main, 0.15),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
                  }
                }}
              >
                {/* Rank & Indicator */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 200 }}>
                  <Chip 
                    label={`#${index + 1}`} 
                    size="small" 
                    sx={{ minWidth: 40, fontWeight: 700 }}
                    color={index < 3 ? 'warning' : 'default'}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {shock.indicator_code.replace(/_/g, ' ')}
                  </Typography>
                </Box>

                {/* Shock Value */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                  {shock.adjusted_shock > 0 ? (
                    <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                  ) : (
                    <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                  )}
                  <Typography 
                    variant="body2" 
                    fontWeight={700}
                    color={shock.adjusted_shock > 0 ? 'success.main' : 'error.main'}
                  >
                    {shock.adjusted_shock > 0 ? '+' : ''}{shock.adjusted_shock.toFixed(2)}
                  </Typography>
                </Box>

                {/* Weight Progress */}
                <Box sx={{ minWidth: 180 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('aiNews.shock.shockWeight')}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {formatPercent(shock.shock_weight)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={shock.shock_weight * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.grey[500], 0.2),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: shock.gating_triggered ? theme.palette.warning.main : theme.palette.grey[400]
                      }
                    }}
                  />
                </Box>

                {/* Articles */}
                <Chip 
                  label={`${shock.article_count} ${t('aiNews.shock.articles')}`}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 100 }}
                />

                {/* Status */}
                <Chip
                  label={getShockLabel(shock)}
                  size="small"
                  color={shock.gating_triggered ? 'warning' : 'default'}
                  icon={shock.gating_triggered ? <BoltIcon /> : undefined}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ShockDashboard;

